import { DatePipe } from "@angular/common";
import { Injectable } from "@angular/core";
import { BusinessUnit, PatientDetails } from "../../models/dto/patient-details";
import { RawRequest } from "../dicom-upload/dicom-upload.service";
import { StowrsInputElement } from "../dicom-upload/stowrs-input-element";
import { Guid } from '@se/se.ui.html5';
import { RecordImageSequence, RecordSeriesSequence, RecordStudySequence, VnaDicomRequest } from "../../models/vna-dicom-web/vna-dicom-request.classes";
import { Command } from "../../models/vna-dicom-web/enum";
import { SendingStatus } from "../dicom-upload/sending-status";
import { VnaSetRequestClient } from "./vna-set-request-client.service";
import { BehaviorSubject, finalize } from "rxjs";
import { IRecordSeriesSequence, IRecordStudySequence } from "../../models/vna-dicom-web/vna-dicom-request.interfaces";

@Injectable({
    providedIn: 'root',
})

export class VnaSetRequestService {

    public showSpinner$: BehaviorSubject<boolean> = new BehaviorSubject(false);

    private setRequestMap: Map<string, VnaDicomRequest> = new Map<string, VnaDicomRequest>();

    constructor(private setRequestClientService: VnaSetRequestClient,
                public datepipe: DatePipe) {

    }

    addRequestItems(idStudy: string, data: VnaDicomRequest) {
        let reqElement: VnaDicomRequest | undefined = this.setRequestMap.get(idStudy);
        this.setRequestMap.set(idStudy, data);
    }

    deleteRequestItems(idStudy: string) {
        this.setRequestMap.delete(idStudy);
    }

    deleteAllRequestItems() {
        this.setRequestMap = new Map<string, VnaDicomRequest>();
    }

    getRequestItems() {
        return this.setRequestMap;
    }

    groupByStudy(elementToSend: StowrsInputElement[], patientInfo: PatientDetails): RawRequest[] {
        let group: Array<RawRequest> = [];
        elementToSend.forEach((elem, index) => {
            if (group.length == 0) {
                let element: RawRequest = {
                    id: elem.studyId,
                    values: [elementToSend[0]],
                    patientDetails: patientInfo
                }
                group.push(element);
            } else {
                let isPresentStudy: boolean = group.some((valRaw) => valRaw.id === elem.studyId);
                if (isPresentStudy) {
                    let indexGroup: number = group.findIndex((raw) => raw.id === elem.studyId);
                    group[indexGroup].values.push(elementToSend[index]);
                } else {
                    let element: RawRequest = {
                        id: elem.studyId,
                        values: [elementToSend[index]],
                        patientDetails: patientInfo
                    }
                    group.push(element);
                }
            }
        });
        return group;
    }

    buildRequestInitial(study: RawRequest, patientInfo: PatientDetails, sendingApplicationValue: string, command: Command) {
        let recordImagesMap = new Map<string, RecordImageSequence[]>();
        let recordStudyArr: Array<IRecordStudySequence> = [];

        study.values.forEach((file: StowrsInputElement) => { //values n° file
            let recImages: RecordImageSequence = this.recordImageSequence(file);
            if (recordImagesMap.has(file.body.metadata['x0020000e'])) {
                let recArr: Array<RecordImageSequence> = recordImagesMap.get(file.body.metadata['x0020000e']);
                recArr.push(recImages);
            } else {
                let recordImg: Array<RecordImageSequence> = [];
                recordImg.push(recImages);
                recordImagesMap.set(file.body.metadata['x0020000e'], recordImg);
            }         
        })

        let recSeries: Array<IRecordSeriesSequence> = Array.from(recordImagesMap, ([seriesInstanceUID, recordImageSequence]) => ({ seriesInstanceUID, recordImageSequence }));
        let recStudy: IRecordStudySequence = this.recordStudySequence(study.values[0], recSeries);
        let datePatient = this.datepipe.transform(patientInfo.BirthDate, 'yyyyMMdd');
        recordStudyArr.push(recStudy);

        let dicomRequest = new VnaDicomRequest(
            command,
            sendingApplicationValue,
            this.mapBUValue(patientInfo.BusinessUnit),
            this.generateRequestUID(study),
            patientInfo.Surname + '^' + patientInfo.Name,
            patientInfo.PatientId,
            patientInfo.Issuer,
            patientInfo.Sex,
            datePatient,
            recordStudyArr
        );
        this.setRequestMap.set(recStudy.studyInstanceUID, dicomRequest);
    }

    private generateRequestUID(study: RawRequest): string {
        let subs = study.id.substring(0, 25);
        let subStringId = this.dotSubstring(subs);
        let uuid = new Guid().toString().split('-');
        let numbUuid1 = BigInt("0x" + uuid[0]);
        let numbUuid2 = BigInt("0x" + uuid[1]);
        let numbUuid3 = BigInt("0x" + uuid[2]);
        let numbUuid4 = BigInt("0x" + uuid[3]);
        let numbUuid5 = BigInt("0x" + uuid[4]);

        let time = new Date();
        let formatTime = this.datepipe.transform(time, 'yyyyMMddHHmmssSSS')
        let requestUID = subStringId + numbUuid1 + '.' + numbUuid3 + '.' + formatTime;
        subs = '';

        return requestUID;
    }

    private dotSubstring(subs: string): string {
        return subs.slice(-1) === '.' ? subs : subs + '.';
    }

    private recordImageSequence(file: StowrsInputElement): RecordImageSequence {
        let record = new RecordImageSequence(
            file.body.metadata.sopClassUID,
            file.body.metadata['x00080018']
        );
        return record;
    }

    private recordStudySequence(file: StowrsInputElement, recordSeriesArray: Array<RecordSeriesSequence> ): RecordStudySequence {
        let record = new RecordStudySequence(
            recordSeriesArray,
            file.body.metadata['x0020000d'],
            file.body.metadata.accessionNumber,
            file.body.metadata['x00200010'],
            file.body.metadata.studyDescription,
            file.body.metadata['x00080020'],
            file.body.metadata['x00080060'],
        )

        let dateTimeStudy = this.datepipe.transform(new Date(record.studyDate).toString(), 'yyyyMMdd');
        record.studyDate = dateTimeStudy;
        
        return record;
    }

    private mapBUValue(bu: BusinessUnit): BusinessUnit {
        let businessUnit: BusinessUnit = {
            id: bu.id,
            code: bu.code,
            description: bu.description
        }
        return businessUnit;
    }

    sendRequest(requestDicomVNA: VnaDicomRequest) {
        this.showSpinner$.next(true);
        return this.setRequestClientService.setRequest(requestDicomVNA).pipe(
            finalize(() => this.showSpinner$.next(false))
        )  
    }

    buildRequestAfterUpload(sendingInstances: Map<string, SendingStatus[]>) {
        let errorHttp: boolean;
        let errorInstance: boolean;
        let requestAfterUpload: Map<string, VnaDicomRequest> = new Map<string, VnaDicomRequest>();
        requestAfterUpload = this.getRequestItems();
        sendingInstances.forEach((item, key) => {
            errorHttp = item.every((el) => el.httpError);
            errorInstance = item.every((el) => el.instanceFailure);
            if (!errorHttp && !errorInstance) {
                let item: VnaDicomRequest = requestAfterUpload.get(key);
                item.command = Command.End;
                requestAfterUpload.set(key, item);
            } else {
                let item: VnaDicomRequest = requestAfterUpload.get(key);
                item.command = Command.Failed;
                requestAfterUpload.set(key, item);
            }
        })
    }
}
